import { FC, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import MultiSelectDropdown from '../lpiReports/MultiSelectDropdown';
import { useDialogA11y } from '../../components/dialog/useDialogA11y';
import { AdvancedFilters, EMISSION_SOURCE_OPTIONS } from './types';
import './AdvancedFilterDrawer.scss';

let drawerInstanceId = 0;

interface SessionTokens {
  jwtToken: string;
  sessionId: string;
}

interface AdvancedFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: AdvancedFilters) => void;
  onReset: () => void;
  filters: AdvancedFilters;
  getSessionTokens: () => Promise<SessionTokens>;
  /**
   * Called once the TOC key->label map is fetched, so a parent that also
   * renders FilterPills can resolve TOC pills to their display label instead
   * of the canonical key the pill's value carries for removal. Optional —
   * omit it and nothing here changes.
   */
  onTocsLoaded?: (tocs: Array<{ key: string; label: string }>) => void;
}

const AdvancedFilterDrawer: FC<AdvancedFilterDrawerProps> = ({
  isOpen,
  onClose,
  onApply,
  onReset,
  filters,
  getSessionTokens,
  onTocsLoaded,
}) => {
  const { t } = useTranslation();

  const dialogRef = useRef<HTMLDivElement>(null);
  const [titleId] = useState(() => {
    drawerInstanceId += 1;
    return `filter-drawer-title-${drawerInstanceId}`;
  });

  useDialogA11y({ isOpen, onClose, containerRef: dialogRef });

  // Local state for draft filter selections (applied on "Apply" click)
  const [localFilters, setLocalFilters] = useState<AdvancedFilters>(filters);

  // Sync local state when parent filters change (e.g. pill removal)
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Options state for each dropdown (lazy-loaded)
  const [carriers, setCarriers] = useState<string[]>([]);
  // const [fuelTypes, setFuelTypes] = useState<string[]>([]); // hidden for now
  const [modalities, setModalities] = useState<string[]>([]);
  const [shippers, setShippers] = useState<string[]>([]);
  const [tocs, setTocs] = useState<Array<{ key: string; label: string }>>([]);
  const [tocOverCap, setTocOverCap] = useState(false);

  // Loading states
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(false);
  // const [isLoadingFuelTypes, setIsLoadingFuelTypes] = useState(false); // hidden for now
  const [isLoadingModalities, setIsLoadingModalities] = useState(false);
  const [isLoadingShippers, setIsLoadingShippers] = useState(false);
  const [isLoadingTocs, setIsLoadingTocs] = useState(false);

  // Track which dropdown is open (only one at a time)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Fetch helpers
  const fetchCarriers = useCallback(async () => {
    if (carriers.length > 0) return; // already loaded
    setIsLoadingCarriers(true);
    try {
      const { jwtToken, sessionId } = await getSessionTokens();
      const response = await axios.get<{ carriers: string[] }>(
        `${process.env.REACT_APP_LPI_API_URL}/cpi/filters/carriers`,
        { headers: { Authorization: `Session ${jwtToken}`, 'X-Session-Id': sessionId } }
      );
      setCarriers(response.data.carriers || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch carriers:', err);
    } finally {
      setIsLoadingCarriers(false);
    }
  }, [carriers.length, getSessionTokens]);

  // fetchFuelTypes hidden for now
  // const fetchFuelTypes = useCallback(async () => {
  //   if (fuelTypes.length > 0) return;
  //   setIsLoadingFuelTypes(true);
  //   try {
  //     const { jwtToken, sessionId } = await getSessionTokens();
  //     const response = await axios.get<{ fuelTypes: string[] }>(
  //       `${process.env.REACT_APP_LPI_API_URL}/cpi/fuel-types`,
  //       { headers: { Authorization: `Session ${jwtToken}`, 'X-Session-Id': sessionId } }
  //     );
  //     setFuelTypes(response.data.fuelTypes || []);
  //   } catch (err) {
  //     console.error('Failed to fetch fuel types:', err);
  //   } finally {
  //     setIsLoadingFuelTypes(false);
  //   }
  // }, [fuelTypes.length, getSessionTokens]);

  const fetchModalities = useCallback(async () => {
    if (modalities.length > 0) return;
    setIsLoadingModalities(true);
    try {
      const { jwtToken, sessionId } = await getSessionTokens();
      const response = await axios.get<{ modalities: { transport: string; modality: string; modalityDetail: string }[] }>(
        `${process.env.REACT_APP_LPI_API_URL}/cpi/modalities`,
        { headers: { Authorization: `Session ${jwtToken}`, 'X-Session-Id': sessionId } }
      );
      // Extract unique modality names from the objects
      const uniqueModalities = Array.from(new Set((response.data.modalities || []).map((m) => m.modality)));
      setModalities(uniqueModalities);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch modalities:', err);
    } finally {
      setIsLoadingModalities(false);
    }
  }, [modalities.length, getSessionTokens]);

  const fetchShippers = useCallback(async () => {
    if (shippers.length > 0) return;
    setIsLoadingShippers(true);
    try {
      const { jwtToken, sessionId } = await getSessionTokens();
      const response = await axios.get<{ shipperIds: string[] }>(
        `${process.env.REACT_APP_LPI_API_URL}/cpi/filters/shippers`,
        { headers: { Authorization: `Session ${jwtToken}`, 'X-Session-Id': sessionId } }
      );
      setShippers(response.data.shipperIds || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch shippers:', err);
    } finally {
      setIsLoadingShippers(false);
    }
  }, [shippers.length, getSessionTokens]);

  const fetchTocs = useCallback(async () => {
    if (tocs.length > 0 || tocOverCap) return;
    setIsLoadingTocs(true);
    try {
      const { jwtToken, sessionId } = await getSessionTokens();
      const response = await axios.get<{
        tocs: Array<{ key: string; label: string }>;
        overCap: boolean;
      }>(
        `${process.env.REACT_APP_LPI_API_URL}/cpi/filters/tocs`,
        { headers: { Authorization: `Session ${jwtToken}`, 'X-Session-Id': sessionId } }
      );
      setTocs(response.data.tocs || []);
      setTocOverCap(Boolean(response.data.overCap));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch TOCs:', err);
    } finally {
      setIsLoadingTocs(false);
    }
  }, [tocs.length, tocOverCap, getSessionTokens]);

  // The over-cap notice replaces the TOC dropdown entirely (see render below),
  // so the dropdown's own lazy on-open fetch would never run while over cap —
  // we'd never learn the tenant is over cap in the first place. Fetch once on
  // mount instead, so overCap status is known before the user ever opens it.
  useEffect(() => {
    fetchTocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Report the resolved key->label list up once it's loaded, so a parent
  // rendering FilterPills alongside this drawer can display TOC pills by
  // label instead of the raw key. Only fires when there's something to
  // report — deliberately not keyed on `onTocsLoaded` itself, so an inline
  // arrow function passed by the parent doesn't retrigger this every render.
  useEffect(() => {
    if (tocs.length > 0) {
      onTocsLoaded?.(tocs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tocs]);

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleReset = () => {
    const empty: AdvancedFilters = {
      carrierIds: [],
      fuelTypes: [],
      modalities: [],
      emissionSources: [],
      shipperIds: [],
      tocs: [],
    };
    setLocalFilters(empty);
    onReset();
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      {isOpen && <div className="advanced-filter-drawer__overlay" onClick={onClose} />}

      {/* Drawer */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`advanced-filter-drawer ${isOpen ? 'advanced-filter-drawer--open' : ''}`}
      >
        {/* Header */}
        <div className="advanced-filter-drawer__header">
          <h3 id={titleId}>{t('cpi.advancedFilters')}</h3>
          <button type="button" className="advanced-filter-drawer__close" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="advanced-filter-drawer__body">
          <MultiSelectDropdown
            label={t('cpi.advancedFilters.carrier')}
            options={carriers}
            selected={localFilters.carrierIds}
            onChange={(val) => setLocalFilters((prev) => ({ ...prev, carrierIds: val }))}
            placeholder={t("All")}
            isLoading={isLoadingCarriers}
            onOpen={() => { setOpenDropdown('carrier'); fetchCarriers(); }}
            onClose={() => setOpenDropdown(null)}
            menuIsOpen={openDropdown === 'carrier'}
            showSelectAll={false}
            minWidth="100%"
          />

          {/* Fuel Type filter hidden for now */}

          <MultiSelectDropdown
            label={t('cpi.advancedFilters.modality')}
            options={modalities}
            selected={localFilters.modalities}
            onChange={(val) => setLocalFilters((prev) => ({ ...prev, modalities: val }))}
            placeholder={t("All")}
            isLoading={isLoadingModalities}
            onOpen={() => { setOpenDropdown('modality'); fetchModalities(); }}
            onClose={() => setOpenDropdown(null)}
            menuIsOpen={openDropdown === 'modality'}
            showSelectAll={false}
            minWidth="100%"
          />

          <MultiSelectDropdown
            label={t('cpi.advancedFilters.emissionSource')}
            options={EMISSION_SOURCE_OPTIONS}
            selected={localFilters.emissionSources}
            onChange={(val) => setLocalFilters((prev) => ({ ...prev, emissionSources: val }))}
            placeholder={t("All")}
            isLoading={false}
            onOpen={() => setOpenDropdown('emissionSource')}
            onClose={() => setOpenDropdown(null)}
            menuIsOpen={openDropdown === 'emissionSource'}
            showSelectAll={false}
            minWidth="100%"
          />

          <MultiSelectDropdown
            label={t('cpi.advancedFilters.shipper')}
            options={shippers}
            selected={localFilters.shipperIds}
            onChange={(val) => setLocalFilters((prev) => ({ ...prev, shipperIds: val }))}
            placeholder={t("All")}
            isLoading={isLoadingShippers}
            onOpen={() => { setOpenDropdown('shipper'); fetchShippers(); }}
            onClose={() => setOpenDropdown(null)}
            menuIsOpen={openDropdown === 'shipper'}
            showSelectAll={false}
            minWidth="100%"
          />

          {tocOverCap ? (
            <div className="advanced-filter-drawer__notice">
              {t('cpi.advancedFilters.tocOverCap')}
            </div>
          ) : (
            <MultiSelectDropdown
              label={t('cpi.advancedFilters.toc')}
              options={tocs.map((o) => o.key)}
              optionLabels={Object.fromEntries(tocs.map((o) => [o.key, o.label]))}
              selected={localFilters.tocs}
              onChange={(val) => setLocalFilters((prev) => ({ ...prev, tocs: val }))}
              placeholder={t('All')}
              isLoading={isLoadingTocs}
              onOpen={() => { setOpenDropdown('toc'); fetchTocs(); }}
              onClose={() => setOpenDropdown(null)}
              menuIsOpen={openDropdown === 'toc'}
              showSelectAll={false}
              minWidth="100%"
            />
          )}
        </div>

        {/* Footer */}
        <div className="advanced-filter-drawer__footer">
          <button type="button" className="btn btn-outline-secondary" onClick={handleReset}>
            {t('cpi.advancedFilters.reset')}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleApply}>
            {t('cpi.advancedFilters.apply')}
          </button>
        </div>
      </div>
    </>
  );
};

export default AdvancedFilterDrawer;
